import * as Sentry from '@sentry/react'
import axios from 'axios'
import { API_BASE_URL } from '../config'
import { reportServiceUnavailable } from './serviceStatus'
import { emitToast } from '../lib/toastBus'
import { disconnectAllSockets } from '../lib/socketRegistry'

export { API_BASE_URL }
export const TOKEN_KEY = 'rapidry_admin_token'

const NETWORK_ERROR_TOAST =
  'Network error. Could not reach the server, check your connection.'

// Collapse identical network-error toasts so a dashboard of parallel failing
// requests does not spam the operator during a brief backend blip.
let lastNetworkToastAt = 0
const NETWORK_TOAST_COOLDOWN_MS = 8_000

const emitNetworkErrorToast = () => {
  const now = Date.now()
  if (now - lastNetworkToastAt < NETWORK_TOAST_COOLDOWN_MS) return
  lastNetworkToastAt = now
  emitToast(NETWORK_ERROR_TOAST, 'error')
}

/**
 * Canonical response unwrapper. The backend wraps payloads as `{ success, data }`;
 * axios wraps the HTTP body as `response.data`. Callers pass `response.data`
 * (the body) here, and this returns its inner `.data` when present, else the
 * value itself. This is the single source of truth - do not re-implement locally.
 */
export const unwrap = <T,>(response: T | { data?: T }): T => {
  if (response && typeof response === 'object' && 'data' in response) {
    return (response as { data?: T }).data as T
  }
  return response as T
}

const api = axios.create({
  baseURL: API_BASE_URL,
})

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY)

  if (token) {
    config.headers = config.headers ?? {}
    config.headers.Authorization = `Bearer ${token}`
  }

  // Generate a unique Request ID for observability (Requirement 11.1)
  config.headers['X-Request-Id'] = crypto.randomUUID()

  const method = (config.method || 'get').toLowerCase()
  const url = config.url || ''
  // `put` is in the list for the B2B billing writes below, which replace a
  // room's services rather than patching them. The existing B2C entries are all
  // POST/PATCH paths, so widening the method list cannot make them match a
  // request they did not match before.
  const requiresIdempotency =
    ['post', 'patch', 'put'].includes(method) &&
    (
      /^\/admin\/orders\/[^/]+\/assign$/i.test(url) ||
      /^\/admin\/complaints\/[^/]+\/resolve$/i.test(url) ||
      /^\/admin\/orders\/[^/]+\/final-bill(?:\/resend)?$/i.test(url) ||
      /^\/admin\/orders\/[^/]+\/reschedule\/(?:confirm|decline)$/i.test(url) ||
      // B2B billing. An operator double-taps Save; the server is idempotent by
      // construction (these are replaces), and the key makes a retried request
      // that already succeeded replay rather than run twice.
      /^\/admin\/b2b\/rooms\/[^/]+\/orders$/i.test(url) ||
      /^\/admin\/b2b\/cycles\/[^/]+\/orders$/i.test(url) ||
      /^\/admin\/b2b\/clients\/[^/]+\/invoice$/i.test(url)
    )

  if (requiresIdempotency && !config.headers['Idempotency-Key']) {
    config.headers['Idempotency-Key'] = crypto.randomUUID()
  }

  Sentry.addBreadcrumb({
    category: 'http',
    type: 'http',
    data: {
      phase: 'request',
      method: config.method?.toUpperCase(),
      url: sanitizeUrl(config.url || ''),
    },
    level: 'info',
  })

  return config
})

api.interceptors.response.use(
  (response) => {
    Sentry.addBreadcrumb({
      category: 'http',
      type: 'http',
      data: {
        method: response.config.method?.toUpperCase(),
        url: sanitizeUrl(response.config.url || ''),
        status_code: response.status,
      },
      level: 'info',
    })
    return response
  },
  (error) => {
    Sentry.addBreadcrumb({
      category: 'http',
      type: 'http',
      data: {
        method: error?.config?.method?.toUpperCase(),
        url: sanitizeUrl(error?.config?.url || ''),
        status_code: error?.response?.status,
      },
      level: 'error',
    })

    const status = error?.response?.status

    // The admin API is unreachable. Show the error screen rather than letting
    // this fall through to the generic 5xx toast, which would stack one failure
    // notice per in-flight request on top of a dashboard that cannot load.
    // Checked before everything else so no other branch can claim it first.
    if (status === 503) {
      reportServiceUnavailable()
      return Promise.reject(error)
    }

    if (status === 401) {
      // Don't logout-loop on the login page (a bad-credentials attempt 401s).
      if (window.location.pathname !== '/login') {
        // Tear the live socket down so it stops reconnecting with a stale token,
        // then run the canonical logout (clears storage + Sentry + redirects).
        disconnectAllSockets()
        void import('../store/authStore').then((m) => m.useAuthStore.getState().logout())
      }
    } else if (!error?.response) {
      // No HTTP response at all - network/CORS/timeout. Never leave it silent.
      emitNetworkErrorToast()
    } else if (status >= 400) {
      /*
       * Surface 4xx/5xx so a drifted/failed call is never fully silent. Pages
       * keep their own inline error states in addition to this.
       *
       * EXCEPT B2B, which opts out (UX-9). This toast prints `data.message`
       * verbatim, and `errorHandler.js` returns `err.message` verbatim in every
       * environment - so an unhandled pg error would put its own SQL on the
       * counter screen, and a body-less failure would read "Request failed
       * (409)." Every B2B screen routes its failures through
       * features/b2b/errorCopy.ts instead, which is also why suppressing this
       * removes a duplicate: the operator was getting the raw toast AND the
       * written sentence for the same failure.
       */
      const isB2b = /\/admin\/b2b\//i.test(error?.config?.url || '')
      if (!isB2b) {
        const data = error.response.data as { message?: string; error?: string } | undefined
        emitToast(data?.message || data?.error || `Request failed (${status}).`, 'error')
      }
    }

    return Promise.reject(error)
  },
)

function sanitizeUrl(url: string): string {
  return url.replace(/token=[^&]+/gi, 'token=[REDACTED]')
}

export interface DashboardOrderParams {
  status?: string
  search?: string
  date_from?: string
  date_to?: string
  date_range?: string
  zone?: string
  payment_method?: string
  /** Only COD orders that are still unpaid (see OrdersSummary). */
  pending_cod?: 1
  /**
   * Only DELIVERED COD orders still unpaid - the reconciliation set. A subset of
   * pending_cod; the backend applies the stricter scope when both are sent.
   */
  delivered_unmarked_cod?: 1
  page?: number
  limit?: number
  order?: string
}

/**
 * Backend-aggregated totals for the Orders page summary row
 * (GET /admin/orders/summary). Computed with COUNT/SUM in Postgres over the SAME
 * filters as the list, so the header can never disagree with the rows and the
 * page never has to fetch every order just to add them up.
 *
 * The three money figures are deliberately different questions:
 *   order_value      - worth of the orders placed in this window (final amounts)
 *   calculated_value - the same orders before any manual adjustment
 *   amount_collected - money actually received on them
 */
export interface OrdersSummary {
  range: { date_range: string; date_from: string | null; date_to: string | null }
  orders_count: number
  order_value: number
  calculated_value: number
  amount_collected: number
  /** Orders in the window excluded from order_value (cancelled/failed/refunded). */
  excluded_orders_count: number
  pending_cod: { count: number; amount: number }
  pending_cod_all_time: { count: number; amount: number }
}

export interface OrdersCalendarParams {
  month: string
}

/**
 * One day of the SALES calendar. `orders_placed` / `order_revenue` are the
 * primary pair and are keyed on when the order was PLACED; the pickup and
 * delivery fields are operational and never stand in for them.
 * See docs/business-date-dimensions.md.
 */
export interface OrdersCalendarEntry {
  /** PRIMARY: orders whose placement timestamp falls on this IST day. */
  orders_placed: number
  /** PRIMARY: final value of exactly those orders. Not collections. */
  order_revenue: number
  /** Placed this day, later cancelled. Excluded from the two above. */
  cancelled_orders?: number
  /** OPERATIONAL: bags collected this day. */
  pickups: number
  /** OPERATIONAL: value of those bags - workload, not sales. */
  pickup_value?: number
  deliveries: number
  cancelled_pickups?: number
  /** @deprecated the pickup-day value; read `order_revenue` for sales. */
  total_revenue?: number
  /** @deprecated read `orders_placed`. */
  placed_orders?: number
  /** @deprecated read `order_revenue`. */
  placed_value?: number
  orders: Array<{
    id: string
    order_number?: string
    customer_name?: string | null
    status?: string
    total?: number
  }>
}

export interface OrdersMapParams {
  status?: string
}

export interface OrdersMapOrder {
  id: string
  order_number: string
  customer_name?: string | null
  status: string
  lat: number
  lng: number
  address_line?: string | null
  agent_name?: string | null
}

export interface HolidayEntry {
  id: string
  date: string
  name: string
  type?: string
}

export interface CustomerParams {
  search?: string
  tag?: string
  sort?: string
  order?: 'asc' | 'desc'
  page?: number
  limit?: number
}

export interface CreateAgentPayload {
  name: string
  phone: string
  email?: string
  zone?: string
}

export interface CreatePartnerPayload {
  name: string
  phone: string
  email?: string
  address: string
  zone?: string
}

export interface CreateCustomerPayload {
  name: string
  phone: string
  email?: string
  address_line?: string
  city?: string
  pincode?: string
}

export interface CreateAdminOrderPayload {
  customer_id: string
  address_id?: string
  items: Array<{
    service_item_id: string
    quantity: number
    item_count?: number | null
  }>
  payment_mode: 'cod' | 'online'
  admin_notes?: string
  special_instructions?: string
  pickup_date?: string
  pickup_slot?: string
  delivery_date?: string
  delivery_slot?: string
  coupon_code?: string
}

export interface CreateCustomerAddressPayload {
  label?: string
  line1?: string
  line2?: string
  area?: string
  landmark?: string
  pincode?: string
  city?: string
  is_default?: boolean
}

export interface ReorderPayload {
  pickup_date: string
  pickup_slot: string
  payment_mode?: 'cod' | 'online'
  admin_notes?: string
}

export interface EditOrderPayload {
  items?: Array<{ service_item_id: string; quantity: number; item_count?: number | null }>
  pickup_date?: string
  pickup_slot?: string
  delivery_date?: string
  delivery_slot?: string
  admin_notes?: string
  special_instructions?: string
  payment_mode?: string
  address_id?: string
  // undefined = leave unchanged; '' = remove coupon; 'CODE' = apply/replace.
  coupon_code?: string
  edit_reason?: string
}

export interface UpdateCustomerTagsPayload {
  tags?: string[]
  customer_notes?: string | null
}

export interface LoyaltyAdjustmentPayload {
  points: number
  type: 'bonus' | 'deduction'
  reason?: string
}

export interface CreateCouponPayload {
  code: string
  discount_type: 'flat' | 'percent'
  discount_value: number
  min_order?: number
  max_discount?: number | null
  expires_at?: string | null
  usage_limit?: number | null
  is_active?: boolean
  per_customer_limit?: number | null
}

export type UpdateCouponPayload = Partial<CreateCouponPayload>

export interface WhatsAppLinkPayload {
  template?: string
  custom_message?: string
  extra_params?: Record<string, unknown>
}

export interface AgentWhatsAppLinkPayload {
  template?: string
  order_id?: string
  custom_message?: string
}

export interface ActivityLogParams {
  page?: number
  limit?: number
  entity_type?: string
  action?: string
}

export interface ComplaintParams {
  status?: string
  category?: string
  priority?: string
  search?: string
  page?: number
  limit?: number
}

export interface ExpenseParams {
  date_from?: string
  date_to?: string
  category?: string
  page?: number
  limit?: number
}

export interface PartnerOrdersParams {
  page?: number
  limit?: number
}

export interface CreateExpensePayload {
  category: string
  description: string
  amount: number
  date: string
  recurring?: boolean
  recurring_frequency?: string | null
}

export interface CreateComplaintPayload {
  customer_id?: string
  customer_phone?: string
  order_id?: string
  order_number?: string
  category: string
  priority: string
  description: string
}

export interface UpdateComplaintPayload {
  status?: string
  resolution?: string
  refund_amount?: number | null
  notes?: string
}

export interface TaxReportParams {
  month: string
  format?: 'json' | 'csv'
}

export type ExpressAnalyticsPeriod = 'today' | 'week' | 'month'

export interface BroadcastFilterPayload {
  tags?: string[]
  min_orders?: number
  last_order_within_days?: number
}

export interface BroadcastPayload {
  message: string
  channel: 'whatsapp'
  filter?: BroadcastFilterPayload
}

export interface BulkStatusPayload {
  order_ids: string[]
  status: string
}

export interface BulkAssignPayload {
  order_ids: string[]
  agent_id: string
}

export interface ServiceZonePayload {
  name: string
  center_lat: number
  center_lng: number
  radius_km: number
  active?: boolean
}

export interface ServiceCenterPayload {
  name: string
  address: string
  lat?: number | null
  lng?: number | null
  max_daily_capacity?: number
  contact_phone?: string
  operating_hours?: Record<string, unknown> | null
  is_active?: boolean
}

export interface DeliveryZonePayload {
  name: string
  base_delivery_fee: number
  surge_multiplier: number
  max_orders_per_day: number
  is_active?: boolean
  polygon: Array<{ lat: number; lng: number }>
  color?: string
}

export interface ServiceableSectorPayload {
  name: string
  centroid_lat: number
  centroid_lng: number
  label?: string | null
  corridor?: string | null
  tier?: string | null
  sector_number?: number | null
  customer_selectable?: boolean
  is_active?: boolean
  display_order?: number
}

export const adminApi = {
  getAgentKycQueue: async (params?: { status?: string; page?: number; limit?: number }) => {
    const response = await api.get('/admin/agents/kyc', { params })
    return response.data
  },

  approveAgentKycAction: async (agentId: string) => {
    const response = await api.post(`/admin/agents/${agentId}/kyc/approve`)
    return response.data
  },

  rejectAgentKycAction: async (agentId: string, reason: string) => {
    const response = await api.post(`/admin/agents/${agentId}/kyc/reject`, { reason })
    return response.data
  },

  login: async (email: string, password: string) => {
    const response = await api.post('/auth/admin-login', { email, password })
    return response.data
  },

  checkServerHealth: async () => {
    const response = await api.get('/health')
    return response.data
  },

  getMe: async () => {
    const response = await api.get('/auth/me')
    return response.data
  },

  requestAdminPasswordReset: async (email: string) => {
    const response = await api.post('/auth/admin/forgot-password', { email })
    return response.data
  },

  resetAdminPassword: async (token: string, newPassword: string) => {
    const response = await api.post('/auth/admin/reset-password', {
      token,
      new_password: newPassword,
    })
    return response.data
  },

  getDashboardStats: async () => {
    const response = await api.get('/admin/dashboard')
    return response.data
  },

  getDashboardAlerts: async () => {
    const response = await api.get('/admin/dashboard/alerts')
    return response.data
  },

  getSlaViolations: async () => {
    const response = await api.get('/admin/sla-violations')
    return response.data
  },

  getActivityLog: async (params?: ActivityLogParams) => {
    const response = await api.get('/admin/activity-log', { params })
    return response.data
  },

  getComplaints: async (params?: ComplaintParams) => {
    const response = await api.get('/admin/complaints', { params })
    return response.data
  },

  createComplaint: async (data: CreateComplaintPayload) => {
    const response = await api.post('/admin/complaints', data)
    return response.data
  },

  updateComplaint: async (id: string, data: UpdateComplaintPayload) => {
    const response = await api.patch(`/admin/complaints/${id}`, data)
    return response.data
  },

  getComplaintStats: async () => {
    const response = await api.get('/admin/complaints/stats')
    return response.data
  },

  getExpenses: async (params?: ExpenseParams) => {
    const response = await api.get('/admin/expenses', { params })
    return response.data
  },

  createExpense: async (data: CreateExpensePayload) => {
    const response = await api.post('/admin/expenses', data)
    return response.data
  },

  deleteExpense: async (id: string) => {
    const response = await api.delete(`/admin/expenses/${id}`)
    return response.data
  },

  getProfitSummary: async (params?: { date_from?: string; date_to?: string }) => {
    const response = await api.get('/admin/profit', { params })
    return response.data
  },

  getAnalytics: async () => {
    const response = await api.get('/admin/analytics')
    return response.data
  },

  getExpressAnalytics: async (period: ExpressAnalyticsPeriod = 'today') => {
    const response = await api.get('/admin/analytics/express', { params: { period } })
    return response.data
  },

  sendBroadcast: async (data: BroadcastPayload) => {
    const response = await api.post('/admin/broadcast', data)
    return response.data
  },

  getServiceZones: async () => {
    const response = await api.get('/admin/zones')
    return response.data
  },

  createServiceZone: async (data: ServiceZonePayload) => {
    const response = await api.post('/admin/zones', data)
    return response.data
  },

  updateServiceZone: async (id: string, data: Partial<ServiceZonePayload>) => {
    const response = await api.patch(`/admin/zones/${id}`, data)
    return response.data
  },

  deleteServiceZone: async (id: string) => {
    const response = await api.delete(`/admin/zones/${id}`)
    return response.data
  },

  getServiceCenters: async (params?: { search?: string; page?: number; limit?: number; sort?: string; order?: 'asc' | 'desc' }) => {
    const response = await api.get('/admin/service-centers', { params })
    return response.data
  },

  createServiceCenter: async (data: ServiceCenterPayload) => {
    const response = await api.post('/admin/service-centers', data)
    return response.data
  },

  updateServiceCenter: async (id: string, data: Partial<ServiceCenterPayload>) => {
    const response = await api.put(`/admin/service-centers/${id}`, data)
    return response.data
  },

  deleteServiceCenter: async (id: string) => {
    const response = await api.delete(`/admin/service-centers/${id}`)
    return response.data
  },

  getServiceCenterStats: async (id: string) => {
    const response = await api.get(`/admin/service-centers/${id}/stats`)
    return response.data
  },

  getDeliveryZones: async () => {
    const response = await api.get('/admin/delivery-zones')
    return response.data
  },

  createDeliveryZone: async (data: DeliveryZonePayload) => {
    const response = await api.post('/admin/delivery-zones', data)
    return response.data
  },

  updateDeliveryZone: async (id: string, data: Partial<DeliveryZonePayload>) => {
    const response = await api.put(`/admin/delivery-zones/${id}`, data)
    return response.data
  },

  deleteDeliveryZone: async (id: string) => {
    const response = await api.delete(`/admin/delivery-zones/${id}`)
    return response.data
  },

  getServiceableSectors: async () => {
    const response = await api.get('/admin/serviceable-sectors')
    return response.data
  },

  createServiceableSector: async (data: ServiceableSectorPayload) => {
    const response = await api.post('/admin/serviceable-sectors', data)
    return response.data
  },

  updateServiceableSector: async (id: string, data: Partial<ServiceableSectorPayload>) => {
    const response = await api.patch(`/admin/serviceable-sectors/${id}`, data)
    return response.data
  },

  deleteServiceableSector: async (id: string) => {
    const response = await api.delete(`/admin/serviceable-sectors/${id}`)
    return response.data
  },

  getDisputes: async (params?: { search?: string; status?: string; page?: number; limit?: number }) => {
    const response = await api.get('/admin/disputes', { params })
    return response.data
  },

  getDisputeDetail: async (id: string) => {
    const response = await api.get(`/admin/disputes/${id}`)
    return response.data
  },

  createDispute: async (data: { order_id?: string; customer_id?: string; issue_type: string; description?: string }) => {
    const response = await api.post('/admin/disputes', data)
    return response.data
  },

  updateDispute: async (id: string, data: Record<string, unknown>) => {
    const response = await api.patch(`/admin/disputes/${id}`, data)
    return response.data
  },

  addDisputeMessage: async (id: string, message: string) => {
    const response = await api.post(`/admin/disputes/${id}/messages`, { message })
    return response.data
  },

  getOrders: async (params?: DashboardOrderParams) => {
    const response = await api.get('/admin/orders', { params })
    return response.data
  },

  // Aggregated in the database - never derive these by summing the rows on screen.
  getOrdersSummary: async (params?: DashboardOrderParams) => {
    const response = await api.get('/admin/orders/summary', { params })
    return response.data as { data?: OrdersSummary }
  },

  getOrdersCalendar: async (params: OrdersCalendarParams) => {
    const response = await api.get('/admin/orders/calendar', { params })
    return response.data
  },

  getOrdersMapData: async (params?: OrdersMapParams) => {
    const response = await api.get('/admin/orders/map', { params })
    return response.data
  },

  exportOrdersCsv: async (params?: DashboardOrderParams) => {
    const response = await api.get('/admin/orders/export', {
      params,
      responseType: 'blob',
    })
    return response
  },

  getOrderDetail: async (id: string) => {
    const response = await api.get(`/admin/orders/${id}`)
    return response.data
  },

  postFinalBill: async (orderId: string, payload: Record<string, any>) => {
    const response = await api.post(`/admin/orders/${orderId}/final-bill`, payload)
    return response.data
  },

  resendFinalBill: async (orderId: string) => {
    const response = await api.post(`/admin/orders/${orderId}/final-bill/resend`)
    return response.data
  },

  getOrderAuditTrail: async (orderId: string) => {
    const response = await api.get(`/admin/orders/${orderId}/audit`)
    return response.data
  },

  // Service Group Tracking
  getOrderGroupLabels: async (orderId: string) => {
    const response = await api.get(`/admin/orders/${orderId}/group-labels`)
    return response.data
  },
  overrideOrderGroup: async (orderId: string, groupId: string, action: 'advance' | 'fail') => {
    const response = await api.post(`/admin/orders/${orderId}/groups/${groupId}/override`, { action })
    return response.data
  },
  getOrderExceptions: async (orderId: string) => {
    const response = await api.get('/admin/exceptions', { params: { order_id: orderId } })
    return response.data
  },
  updateException: async (id: string, patch: { status?: string; assigned_to?: string | null; resolution?: string }) => {
    const response = await api.patch(`/admin/exceptions/${id}`, patch)
    return response.data
  },

  assignAgent: async (orderId: string, agentId: string) => {
    const response = await api.patch(`/admin/orders/${orderId}/assign`, {
      agent_id: agentId,
    })
    return response.data
  },

  getAvailableAgents: async (orderId: string) => {
    const response = await api.get('/admin/agents/available', { params: { orderId } })
    return response.data
  },

  assignOrderToAgent: async (orderId: string, agentId: string, idempotencyKey: string) => {
    const response = await api.post(`/admin/orders/${orderId}/assign`,
      { agentId },
      { headers: { 'Idempotency-Key': idempotencyKey } },
    )
    return response.data
  },

  confirmReschedule: async (orderId: string) => {
    const response = await api.post(`/admin/orders/${orderId}/reschedule/confirm`)
    return response.data
  },

  declineReschedule: async (orderId: string, reason: string) => {
    const response = await api.post(`/admin/orders/${orderId}/reschedule/decline`, { reason })
    return response.data
  },

  getCapacityStatus: async (date: string, slot?: string) => {
    const response = await api.get('/admin/capacity', { params: { date, slot } })
    return response.data
  },

  updateOrderStatus: async (id: string, status: string, cancellationReason?: string) => {
    const response = await api.patch(`/admin/orders/${id}/status`, {
      status,
      ...(cancellationReason ? { cancellation_reason: cancellationReason } : {}),
    })
    return response.data
  },

  transitionOrderState: async (
    id: string,
    toState: string,
    payload?: Record<string, unknown>,
    idempotencyKey?: string,
  ) => {
    const key =
      idempotencyKey ||
      (typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `idem-${Date.now()}-${Math.random().toString(16).slice(2)}`)

    const response = await api.post(
      `/admin/orders/${id}/transitions`,
      {
        to_state: toState,
        payload: payload || {},
        idempotency_key: key,
      },
      {
        headers: {
          'Idempotency-Key': key,
        },
      },
    )
    return response.data
  },

  updateOrderNotes: async (id: string, adminNotes: string | null) => {
    const response = await api.patch(`/admin/orders/${id}/notes`, {
      admin_notes: adminNotes,
    })
    return response.data
  },

  // Records the weight the hub actually processed for a by-weight order. Pass
  // null to clear it. Does not reprice - use postFinalBill for that.
  updateOrderWeight: async (id: string, weightKg: number | null) => {
    const response = await api.patch(`/admin/orders/${id}/weight`, {
      weight_kg: weightKg,
    })
    return response.data
  },

  /**
   * `payment_method` is how the money actually ARRIVED (cash | upi | online) -
   * not the checkout choice, which the backend keeps separately. It is required
   * when moving an unsettled order to 'paid': the backend refuses a bare
   * "paid" so no order can be settled with no record of how.
   *
   * A second Mark Paid on a settled order is refused with 409 rather than
   * replayed; pass `reconcile: true` only for a deliberate method correction.
   */
  updatePaymentStatus: async (
    id: string,
    paymentStatus: string,
    payload: {
      final_amount_paid?: number
      adjustment_remark?: string
      payment_method?: 'cash' | 'upi' | 'online'
      payment_reference?: string
      reconcile?: boolean
    } = {},
  ) => {
    const response = await api.patch(`/admin/orders/${id}/payment`, {
      payment_status: paymentStatus,
      ...payload,
    })
    return response.data
  },

  getAgents: async () => {
    const response = await api.get('/admin/agents')
    return response.data
  },

  // GET /admin/agents/live - feed for the live-map panel.
  // Returns every online agent with current_lat/lng, last_seen_at,
  // and active_orders_count for marker tooltips.
  getLiveAgents: async () => {
    const response = await api.get('/admin/agents/live')
    return response.data
  },

  createAgent: async (data: CreateAgentPayload) => {
    const response = await api.post('/admin/agents', data)
    return response.data
  },

  suspendAgent: async (id: string) => {
    const response = await api.patch(`/admin/agents/${id}/suspend`)
    return response.data
  },

  approveAgentKyc: async (id: string, status: 'verified' | 'rejected', override?: boolean) => {
    const overrideQuery = override === undefined ? '' : `?override=${override ? 'true' : 'false'}`
    const response = await api.patch(`/admin/agents/${id}/kyc${overrideQuery}`, { status })
    return response.data
  },

  getAgentKycDocuments: async (agentId: string) => {
    const response = await api.get(`/admin/agents/${agentId}/kyc-documents`)
    return response.data
  },

  getAgentDetail: async (id: string) => {
    const response = await api.get(`/admin/agents/${id}`)
    return response.data
  },

  reviewAgentKycDocument: async (agentId: string, docId: string, data: { status?: 'approved' | 'rejected'; review_status?: 'approved' | 'rejected'; review_notes?: string }) => {
    const response = await api.patch(`/admin/agents/${agentId}/kyc-documents/${docId}`, data)
    return response.data
  },

  verifyAgentPaymentDetails: async (agentId: string, data: { status: 'verified' | 'rejected'; reason?: string }) => {
    const response = await api.patch(`/admin/agents/${agentId}/payment-details/verify`, data)
    return response.data
  },

  getAgentEarnings: async (id: string, params?: { date_from?: string; date_to?: string }) => {
    const response = await api.get(`/admin/agents/${id}/earnings`, { params })
    return response.data
  },

  getAgentPerformance: async (id: string) => {
    const response = await api.get(`/admin/agents/${id}/performance`)
    return response.data
  },

  getPayouts: async (params?: { agent_id?: string; status?: string; page?: number; limit?: number }) => {
    const response = await api.get('/admin/agents/payouts', { params })
    return response.data
  },

  createPayout: async (data: { agent_id: string; period_start: string; period_end: string; bonus?: number; deductions?: number; notes?: string }) => {
    const response = await api.post('/admin/agents/payouts', data)
    return response.data
  },

  markPayoutPaid: async (id: string, data: { payment_method?: string; payment_reference?: string; notes?: string }) => {
    const response = await api.patch(`/admin/agents/payouts/${id}/pay`, data)
    return response.data
  },

  createPartner: async (data: CreatePartnerPayload) => {
    const response = await api.post('/admin/partners', data)
    return response.data
  },

  suspendPartner: async (id: string) => {
    const response = await api.patch(`/admin/partners/${id}/suspend`)
    return response.data
  },

  getPartnerPerformance: async (id: string) => {
    const response = await api.get(`/admin/partners/${id}/performance`)
    return response.data
  },

  getPartnerOrders: async (id: string, params?: PartnerOrdersParams) => {
    const response = await api.get(`/admin/partners/${id}/orders`, { params })
    return response.data
  },

  updatePartnerScore: async (id: string, data: { quality_score: number; notes?: string }) => {
    const response = await api.patch(`/admin/partners/${id}/score`, data)
    return response.data
  },

  suspendCustomer: async (id: string) => {
    const response = await api.patch(`/admin/customers/${id}/suspend`)
    return response.data
  },

  blockCustomer: async (id: string) => {
    const response = await api.patch(`/admin/customers/${id}/block`)
    return response.data
  },

  unblockCustomer: async (id: string) => {
    const response = await api.patch(`/admin/customers/${id}/unblock`)
    return response.data
  },

  sendCustomerNotification: async (id: string, payload: { title: string; message: string; data?: Record<string, unknown> }) => {
    const response = await api.post(`/admin/customers/${id}/notify`, payload)
    return response.data
  },

  getCustomers: async (params?: CustomerParams) => {
    const response = await api.get('/admin/customers', { params })
    return response.data
  },

  getCustomerStats: async () => {
    const response = await api.get('/admin/customers/stats')
    return response.data
  },

  getCustomerDetail: async (id: string) => {
    const response = await api.get(`/admin/customers/${id}`)
    return response.data
  },

  updateCustomerTags: async (id: string, data: UpdateCustomerTagsPayload) => {
    const response = await api.patch(`/admin/customers/${id}/tags`, data)
    return response.data
  },

  getCustomerLoyalty: async (id: string) => {
    const response = await api.get(`/admin/customers/${id}/loyalty`)
    return response.data
  },

  getCustomerCLV: async (id: string) => {
    const response = await api.get(`/admin/customers/${id}/clv`)
    return response.data
  },

  adjustCustomerLoyalty: async (id: string, data: LoyaltyAdjustmentPayload) => {
    const response = await api.post(`/admin/customers/${id}/loyalty`, data)
    return response.data
  },

  searchCustomerByPhone: async (phone: string) => {
    const response = await api.get('/admin/customers', {
      params: { search: phone },
    })
    return response.data
  },

  searchCustomersQuick: async (query: string, limit = 10) => {
    const response = await api.get('/admin/customers/search', {
      params: { q: query, limit },
    })
    return response.data
  },

  getPartners: async () => {
    const response = await api.get('/admin/partners')
    return response.data
  },

  getCoupons: async () => {
    const response = await api.get('/admin/coupons')
    return response.data
  },

  createCoupon: async (data: CreateCouponPayload) => {
    const response = await api.post('/admin/coupons', data)
    return response.data
  },

  updateCoupon: async (id: string, data: UpdateCouponPayload) => {
    const response = await api.patch(`/admin/coupons/${id}`, data)
    return response.data
  },

  getCouponRedemptions: async (id: string, page = 1, limit = 20) => {
    const response = await api.get(`/admin/coupons/${id}/redemptions`, { params: { page, limit } })
    return response.data
  },
  deleteCoupon: async (id: string) => {
    const response = await api.delete(`/admin/coupons/${id}`)
    return response.data
  },

  getServices: async () => {
    const response = await api.get('/admin/services')
    return response.data
  },

  getServicesFull: async () => {
    const response = await api.get('/admin/services/full')
    return response.data
  },

  createService: async (data: {
    name: string
    description?: string
    base_price?: number
    price_unit?: string
    delivery_model?: 'standard' | 'express'
    is_active?: boolean
  }) => {
    const response = await api.post('/admin/services', data)
    return response.data
  },

  updateService: async (id: string, data: Record<string, unknown>) => {
    const response = await api.put(`/admin/services/${id}`, data)
    return response.data
  },

  createServiceItem: async (serviceId: string, data: { name: string; price: number; unit?: string; category?: string | null; is_active?: boolean }) => {
    const response = await api.post(`/admin/services/${serviceId}/items`, data)
    return response.data
  },

  updateServiceItem: async (serviceId: string, itemId: string, data: Record<string, unknown>) => {
    const response = await api.put(`/admin/services/${serviceId}/items/${itemId}`, data)
    return response.data
  },

  deleteServiceItem: async (serviceId: string, itemId: string) => {
    const response = await api.delete(`/admin/services/${serviceId}/items/${itemId}`)
    return response.data
  },

  toggleServiceActive: async (id: string, is_active: boolean) => {
    const response = await api.patch(`/admin/services/${id}/active`, { is_active })
    return response.data
  },

  getSettings: async () => {
    const response = await api.get('/admin/settings')
    return response.data
  },

  updateSettings: async (data: Record<string, string>) => {
    const response = await api.patch('/admin/settings', data)
    return response.data
  },

  getHolidays: async (year: number) => {
    const response = await api.get('/admin/holidays', { params: { year } })
    return response.data
  },

  createHoliday: async (data: { date: string; name: string; type?: string }) => {
    const response = await api.post('/admin/holidays', data)
    return response.data
  },

  deleteHoliday: async (id: string) => {
    const response = await api.delete(`/admin/holidays/${id}`)
    return response.data
  },

  updateServicePricing: async (id: string, price: number) => {
    const response = await api.put(`/admin/services/${id}/price`, { price })
    return response.data
  },

  createCustomer: async (data: CreateCustomerPayload) => {
    const response = await api.post('/admin/customers', data)
    return response.data
  },

  createOrderFromAdmin: async (data: CreateAdminOrderPayload) => {
    const response = await api.post('/admin/orders', data)
    return response.data
  },

  getCustomerAddresses: async (customerId: string) => {
    const response = await api.get(`/admin/customers/${customerId}/addresses`)
    return response.data
  },

  createCustomerAddress: async (customerId: string, data: CreateCustomerAddressPayload) => {
    const response = await api.post(`/admin/customers/${customerId}/addresses`, data)
    return response.data
  },

  // Public slot-availability endpoint (no auth required); used for live
  // pickup/delivery slot selection in the admin order flow.
  getSlots: async (date: string) => {
    const response = await api.get('/slots', { params: { date } })
    return response.data
  },

  reorderFromPrevious: async (orderId: string, data: ReorderPayload) => {
    const response = await api.post(`/admin/orders/${orderId}/reorder`, data)
    return response.data
  },

  assignPartner: async (orderId: string, partnerId: string) => {
    const response = await api.patch(`/admin/orders/${orderId}/partner`, {
      partner_id: partnerId,
    })
    return response.data
  },

  editOrder: async (id: string, data: EditOrderPayload) => {
    const response = await api.patch(`/admin/orders/${id}/edit`, data)
    return response.data
  },

  // Admin owner-override: mark an order delivered directly from any stage.
  markOrderDelivered: async (id: string) => {
    const response = await api.post(`/admin/orders/${id}/mark-delivered`)
    return response.data
  },

  getWhatsAppLink: async (orderId: string, body: WhatsAppLinkPayload) => {
    const response = await api.post(`/admin/orders/${orderId}/whatsapp-link`, body)
    return response.data
  },

  getWhatsAppTemplates: async () => {
    const response = await api.get('/admin/whatsapp/templates')
    return response.data
  },

  resendInvoiceWhatsApp: async (orderId: string) => {
    const response = await api.post(`/admin/orders/${orderId}/invoice/whatsapp/resend`)
    return response.data
  },

  getAgentWhatsAppLink: async (agentId: string, body: AgentWhatsAppLinkPayload) => {
    const response = await api.post(`/admin/agents/${agentId}/whatsapp-link`, body)
    return response.data
  },

  getTaxReport: async (params: TaxReportParams) => {
    const response = await api.get('/admin/reports/tax', { params })
    return response.data
  },

  getTaxReportCsv: async (month: string) => {
    const response = await api.get('/admin/reports/tax', {
      params: { month, format: 'csv' },
      responseType: 'blob',
    })
    return response
  },

  bulkUpdateOrderStatus: async (orderIds: string[], status: string) => {
    const response = await api.patch('/admin/orders/bulk-status', {
      order_ids: orderIds,
      status,
    })
    return response.data
  },

  bulkAssignAgent: async (orderIds: string[], agentId: string) => {
    const response = await api.patch('/admin/orders/bulk-assign', {
      order_ids: orderIds,
      agent_id: agentId,
    })
    return response.data
  },

  getCashSummary: async (date?: string, agentId?: string) => {
    const response = await api.get('/admin/cash/summary', {
      params: { date, agent_id: agentId },
    })
    return response.data
  },

  getCashHistory: async (params?: {
    agent_id?: string
    date_from?: string
    date_to?: string
    deposited?: string
    page?: number
    limit?: number
  }) => {
    const response = await api.get('/admin/cash/history', { params })
    return response.data
  },

  markCashDeposited: async (
    id: string,
    data: { deposited_method: string; notes?: string },
  ) => {
    const response = await api.patch(`/admin/cash/${id}/deposit`, data)
    return response.data
  },

  markBulkDeposited: async (
    collectionIds: string[],
    depositedMethod: string,
    notes?: string,
  ) => {
    const response = await api.patch('/admin/cash/bulk-deposit', {
      collection_ids: collectionIds,
      deposited_method: depositedMethod,
      notes,
    })
    return response.data
  },

  getAdminUsers: async () => {
    const response = await api.get('/admin/users/admins')
    return response.data
  },

  createAdminUser: async (data: { name: string; email: string; password: string; admin_role: string }) => {
    const response = await api.post('/admin/users/admins', data)
    return response.data
  },

  updateAdminRole: async (id: string, adminRole: string) => {
    const response = await api.patch(`/admin/users/admins/${id}/role`, { admin_role: adminRole })
    return response.data
  },

  getExpressSlots: async () => {
    const response = await api.get('/admin/express-slots')
    return response.data
  },

  disableExpressSlot: async (slotId: string) => {
    const response = await api.patch(`/admin/express-slots/${slotId}/disable`)
    return response.data
  },

  enableExpressSlot: async (slotId: string) => {
    const response = await api.patch(`/admin/express-slots/${slotId}/enable`)
    return response.data
  },
}

export default api
